import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_PAGE_LIMIT, type ClauseListRequest, type ClauseListResponse, type LibraryClause } from '@clm/shared-types';

import { fetchClauses, manageClause } from '../api/clauses';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { CursorPager } from '../components/CursorPager';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import { EMPTY_CURSOR_STACK, pageNumber, popCursor, pushCursor, type CursorStack } from '../components/cursor-stack';
import { requireSurface } from '../routes/surfaces';

/**
 * Clause library — FR-16, FR-17, NFR-13 · CLM-55.
 *
 * The list of governed master clauses, readable by every authorised user
 * (FR-17's read grant, `GET /v1/clauses`), with the one write this surface owns:
 * **New clause** (`create_clause`). Revising, retiring and the version history
 * live on the detail surface at `/clauses/:clause_code`.
 *
 * ## The primary action renders regardless of the fetch
 *
 * The header, the New clause button and the filter row sit **outside** the
 * panel state machine. In production the API is not yet deployed (B-33), so the
 * list renders "The request did not complete" — and a reviewer must still be
 * able to see what the surface is for. A 501 says which endpoint; a 403 says the
 * role gate refused (B-07: nobody holds `legal_reviewer` in prod yet).
 *
 * ## Every filter is a request parameter
 *
 * `include_inactive` and `category` are sent, and every change resets paging:
 * cursors are minted per filter set (`clauses-cursor.ts`) and a cursor spent on
 * a different set is a 400. Nothing is sorted or filtered over a partial page in
 * this browser.
 *
 * ## Not rendered here
 *
 * No Right/Wrong judgement control, no boost control, no search alias — those
 * are Phase 5: judgements live under each hit on Search and boosts on the Search
 * quality surface (CLM-39); the alias actions are real, on the Aliases panel there (CLM-59).
 */

interface ListState {
  readonly response: ClauseListResponse | null;
  readonly busy: boolean;
  readonly problem: Problem | null;
}

interface Problem {
  readonly kind: ApiProblemKind;
  readonly detail: string | null;
  readonly correlation_id: string | null;
}

function toProblem(error: unknown): Problem {
  return {
    kind: classifyApiError(error),
    detail: error instanceof ApiError ? error.message : null,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

const INITIAL: ListState = { response: null, busy: true, problem: null };

function formatDate(iso: string): string {
  return iso.slice(0, 10);
}

export function ClauseLibrary() {
  const surface = requireSurface('clauses');
  const navigate = useNavigate();

  const [includeInactive, setIncludeInactive] = useState(false);
  const [category, setCategory] = useState('');
  const [stack, setStack] = useState<CursorStack>(EMPTY_CURSOR_STACK);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [state, setState] = useState<ListState>(INITIAL);
  const [reloadNonce, setReloadNonce] = useState(0);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const trimmed = category.trim();
    const request: ClauseListRequest = {
      ...(includeInactive ? { include_inactive: true } : {}),
      ...(trimmed === '' ? {} : { category: trimmed }),
      page: cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor },
    };

    setState((previous) => ({ ...previous, busy: true }));

    void (async () => {
      try {
        const response = await fetchClauses(request, controller.signal);
        if (controller.signal.aborted) return;
        setState({ response, busy: false, problem: null });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({ response: null, busy: false, problem: toProblem(error) });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [includeInactive, category, cursor, reloadNonce]);

  /** A filter change invalidates every cursor: they are minted per filter set. */
  const resetPaging = useCallback(() => {
    setStack(EMPTY_CURSOR_STACK);
    setCursor(undefined);
  }, []);

  const items = state.response?.items ?? [];
  const totals = state.response?.totals ?? null;
  const page = state.response?.page ?? null;
  const filtered = includeInactive || category.trim() !== '';

  const columns: readonly DataTableColumn<LibraryClause>[] = [
    { id: 'code', header: 'Code', cell: (item) => <code>{item.clause_code}</code> },
    { id: 'title', header: 'Title', cell: (item) => item.title },
    { id: 'category', header: 'Category', cell: (item) => item.category ?? <span className="mut">—</span> },
    { id: 'version', header: 'Version', cell: (item) => `v${String(item.version_no)}` },
    {
      id: 'state',
      header: 'State',
      // The word, not only a colour: retired is excluded from ranking.
      cell: (item) => (item.is_active ? <span className="tag acc">Active</span> : <span className="tag out">Retired</span>),
    },
    {
      id: 'edited',
      header: 'Last edited',
      cell: (item) =>
        item.last_edited_by_upn === null || item.last_edited_utc === null ? (
          <span className="mut">— (v1)</span>
        ) : (
          <>
            {item.last_edited_by_upn}
            <br />
            <span className="mut">{formatDate(item.last_edited_utc)}</span>
          </>
        ),
    },
    {
      id: 'purpose',
      header: 'Purpose text',
      cell: (item) => (item.purpose_text.value === null ? <span className="tag out">missing</span> : 'present'),
    },
  ];

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
      </div>

      <NewClauseForm
        onCreated={() => {
          setReloadNonce((n) => n + 1);
        }}
      />

      <div className="card">
        <h3>Library</h3>
        <p>
          Every authorised user may read and reuse these clauses; only Legal reviewers may create,
          revise or retire one (FR-17), and every such write is versioned and audited. Filters are
          sent to the API — nothing here is filtered over a partial page.
        </p>

        <div className="clib-filters">
          <label>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => {
                setIncludeInactive(event.target.checked);
                resetPaging();
              }}
            />
            Include retired
          </label>
          <label>
            Category
            <input
              type="text"
              value={category}
              maxLength={60}
              onChange={(event) => {
                setCategory(event.target.value);
                resetPaging();
              }}
            />
          </label>
        </div>

        {state.busy && state.response === null && state.problem === null ? (
          <p role="status" aria-busy="true">
            Reading the clause library…
          </p>
        ) : null}

        {state.problem ? (
          <>
            <ErrorNotice kind={state.problem.kind} detail={state.problem.detail} correlation_id={state.problem.correlation_id} />
            {state.problem.kind === 'not_implemented' ? (
              <p className="mut">
                Waiting on <code>GET /v1/clauses</code>. The New clause form above still submits to{' '}
                <code>POST /v1/clauses/actions</code> and reports its own outcome.
              </p>
            ) : null}
          </>
        ) : null}

        {state.response ? (
          <>
            {totals ? (
              <div className="totals">
                <div className="total">
                  <span>Current</span>
                  <b>{totals.current}</b>
                </div>
                <div className="total">
                  <span>Active</span>
                  <b>{totals.active}</b>
                </div>
                <div className="total" data-tone={totals.purpose_missing > 0 ? 'warn' : 'plain'}>
                  <span>Missing purpose text</span>
                  <b>{totals.purpose_missing}</b>
                </div>
              </div>
            ) : null}

            <DataTable
              caption="Clause library"
              columns={columns}
              rows={items}
              rowKey={(item) => item.clause_code}
              busy={state.busy}
              activate={{
                header: 'Open',
                label: (item) => `Open ${item.clause_code}`,
                buttonId: (item) => `clib-open-${item.clause_code}`,
                onActivate: (item) => {
                  void navigate(`/clauses/${encodeURIComponent(item.clause_code)}`);
                },
              }}
              empty={
                filtered ? (
                  <p>No clause matches these filters.</p>
                ) : (
                  <>
                    <p>
                      <strong>The library is empty.</strong>
                    </p>
                    <p>Publish a clause from the mining queue or create one here.</p>
                  </>
                )
              }
            />

            {page && items.length > 0 ? (
              <CursorPager
                next_cursor={page.next_cursor}
                historyDepth={stack.cursors.length}
                busy={state.busy}
                pageNumber={pageNumber(stack)}
                returnedCount={page.returned_count}
                totalInScope={page.total_in_scope}
                onNext={() => {
                  const next = page.next_cursor;
                  if (next === null) return;
                  setStack((current) => pushCursor(current, next));
                  setCursor(next);
                }}
                onPrevious={() => {
                  const popped = popCursor(stack);
                  setStack(popped.stack);
                  setCursor(popped.cursor);
                }}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* New clause                                                                  */
/* -------------------------------------------------------------------------- */

type FormPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  | { readonly kind: 'problem'; readonly problem: Problem }
  | { readonly kind: 'created'; readonly clause_code: string; readonly audit_id: string };

/**
 * `create_clause` — the one write on this surface. FR-16 needs a way to seed a
 * clause the corpus never contained; the code-taken check is the same one the
 * mining queue's publish path makes, so the two cannot collide.
 *
 * The API enforces the role (a `contributor` gets 403); the form does not hide
 * itself on a claim — `hasRole` dims, never hides.
 */
function NewClauseForm({ onCreated }: { readonly onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<FormPhase>({ kind: 'idle' });
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [masterText, setMasterText] = useState('');
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const headingId = useId();

  useEffect(() => {
    if (open) headingRef.current?.focus();
  }, [open]);

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPhase({ kind: 'busy' });
    void (async () => {
      try {
        const response = await manageClause({
          action: 'create_clause',
          clause_code: code.trim(),
          title: title.trim(),
          category: category.trim() === '' ? null : category.trim(),
          master_text: masterText,
        });
        setPhase({ kind: 'created', clause_code: response.clause?.clause_code ?? code.trim(), audit_id: response.audit.audit_id });
        setCode('');
        setTitle('');
        setCategory('');
        setMasterText('');
        setOpen(false);
        buttonRef.current?.focus();
        onCreated();
      } catch (error) {
        setPhase({ kind: 'problem', problem: toProblem(error) });
      }
    })();
  };

  return (
    <div className="clib-actions-wrap">
      <div className="clib-actions">
        <button
          ref={buttonRef}
          type="button"
          className="btn"
          aria-expanded={open}
          onClick={() => {
            setOpen((current) => !current);
          }}
        >
          New clause
        </button>
        {phase.kind === 'created' ? (
          <p role="status" className="mut">
            Clause <code>{phase.clause_code}</code> created as v1 · audit <code>{phase.audit_id}</code>.
          </p>
        ) : null}
      </div>

      {open ? (
        <form className="cform" aria-labelledby={headingId} onSubmit={submit}>
          <h3 id={headingId} ref={headingRef} tabIndex={-1}>
            New clause
          </h3>
          <label>
            Code
            <input type="text" value={code} required maxLength={32} pattern="[A-Z0-9][A-Z0-9-]{1,31}" onChange={(e) => setCode(e.target.value)} />
          </label>
          <label>
            Title
            <input type="text" value={title} required maxLength={200} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label>
            Category (optional)
            <input type="text" value={category} maxLength={60} onChange={(e) => setCategory(e.target.value)} />
          </label>
          <label>
            Master text
            <textarea value={masterText} required onChange={(e) => setMasterText(e.target.value)} />
          </label>
          {phase.kind === 'problem' ? (
            <>
              <ErrorNotice
                kind={phase.problem.kind}
                detail={phase.problem.detail}
                correlation_id={phase.problem.correlation_id}
              />
              {phase.problem.kind === 'forbidden' ? (
                <p className="mut">
                  Master text edits need the Legal reviewer role (B-07: not yet granted in production).
                </p>
              ) : null}
              {phase.problem.kind === 'not_implemented' ? (
                <p className="mut">
                  Waiting on <code>POST /v1/clauses/actions</code>.
                </p>
              ) : null}
            </>
          ) : null}
          <div className="cform-acts">
            <button type="submit" className="btn" disabled={phase.kind === 'busy'}>
              Create clause
            </button>
            <button type="button" className="btn sm" onClick={close}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
